import React, { forwardRef, RefObject } from 'react'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import {
  mediaDesktopAndUp,
  mediaLargeScreens,
  mediaMinL,
} from 'uiComponents/constants/customMedia'
import RowSection, { IRowSectionProps } from './RowSection'

type SplitType = '50:50' | '65:35' | '20:80' | '15:85'

export interface IStaticSplitSectionProps
  extends IRowSectionProps,
    IComponentSplit {
  contentStart: React.ReactNode
  contentEnd: React.ReactNode
  refStart?: React.RefObject<HTMLDivElement>
  refEnd?: React.RefObject<HTMLDivElement>
  className?: string
  ref?: RefObject<HTMLElement>
  setMinWidthStart?: boolean
}

interface IComponentSplit {
  split?: SplitType
  splitMedium?: SplitType
  splitLarge?: SplitType
}

const ComponentSplitMap: {
  [split in SplitType]: {
    Start: FlattenSimpleInterpolation
    End: FlattenSimpleInterpolation
  }
} = {
  '50:50': {
    Start: css`
      width: 50%;
    `,
    End: css`
      width: 50%;
    `,
  },
  '65:35': {
    Start: css`
      width: 65%;
    `,
    End: css`
      width: 35%;
    `,
  },
  '20:80': {
    Start: css`
      width: 20%;
    `,
    End: css`
      width: 80%;
    `,
  },
  '15:85': {
    Start: css`
      width: 15%;
    `,
    End: css`
      width: 85%;
    `,
  },
}

export const SectionStart = styled.div<IComponentSplit>`
  ${({ split }) =>
    split ? ComponentSplitMap[split].Start : ComponentSplitMap['50:50'].Start};
  @media ${mediaDesktopAndUp} {
    ${({ splitMedium }) =>
      splitMedium ? ComponentSplitMap[splitMedium].Start : null};
  }
  @media ${mediaLargeScreens} {
    ${({ splitLarge }) =>
      splitLarge ? ComponentSplitMap[splitLarge].Start : null};
  }
`

export const SectionEnd = styled.div<IComponentSplit>`
  ${({ split }) =>
    split ? ComponentSplitMap[split].End : ComponentSplitMap['50:50'].End};
  @media ${mediaDesktopAndUp} {
    ${({ splitMedium }) =>
      splitMedium ? ComponentSplitMap[splitMedium].End : null};
  }
  @media ${mediaLargeScreens} {
    ${({ splitLarge }) =>
      splitLarge ? ComponentSplitMap[splitLarge].End : null};
  }
`

const StyledRowSection = styled(RowSection)<
  Partial<IStaticSplitSectionProps & { setMinWidthStart?: boolean }>
>`
  width: 100%;
  display: flex;
  ${SectionStart} {
    margin-right: calc(2.7vw + 5px);
    ${({ setMinWidthStart }) =>
      setMinWidthStart &&
      css`
        min-width: 180px;
      `};

    @media ${mediaMinL} {
      margin-right: 45px;
    }
  }
  ${SectionEnd} {
    margin-left: calc(2.7vw + 5px);
    @media ${mediaMinL} {
      margin-left: 45px;
    }
  }
`

const StaticSplitSection = forwardRef<HTMLElement, IStaticSplitSectionProps>(
  function StaticSplitSectionComponent(
    {
      dataTestId,
      contentStart,
      contentEnd,
      refStart,
      refEnd,
      split,
      splitMedium,
      splitLarge,
      ...props
    },
    ref,
  ) {
    return (
      <StyledRowSection
        data-testid={dataTestId ? dataTestId : 'StaticSplitSection'}
        ref={ref as RefObject<HTMLElement>}
        {...props}
      >
        <SectionStart
          data-testid="SectionStart"
          ref={refStart}
          split={split}
          splitMedium={splitMedium}
          splitLarge={splitLarge}
        >
          {contentStart}
        </SectionStart>
        <SectionEnd
          data-testid="SectionEnd"
          ref={refEnd}
          split={split}
          splitMedium={splitMedium}
          splitLarge={splitLarge}
        >
          {contentEnd}
        </SectionEnd>
      </StyledRowSection>
    )
  },
)

export default StaticSplitSection
